// visualizer.js
// ============================================================================
// CPC Visualizer – logika a frontenden
// - UI frakciók kezelése
// - módszer beállítások olvasása (t0, tss, stb.)
// - komponensek beolvasása és backend számítás meghívása
// - csúcsparaméterek számítása + t0 korrekció
// - frakció integrálás (jelenlegi: fix lépcső, baloldali kvadratúra)
// - DUPLIKÁCIÓ ELLENI VÉDELEM: UI-lock + stale guard + AbortController
// ============================================================================

import axios from 'axios';
import * as plot from './plot';

/* ============================================================================
   FUTÁS-KEZELŐ: UI-lock + stale guard + AbortController
   ----------------------------------------------------------------------------
   Miért kell?
   - Gyors egymás utáni input-eventek (pl. dead volume slider) több párhuzamos
     számítást indítanak → régi futások később írhatnak a DOM-ra → "duplázódás".
   Mit csinál?
   - beginRun(): leállítja az előző kérést, letiltja a UI-t, új runId/Abort signal
   - endRun(runId): csak a legfrissebb futás enged vissza UI-t
   - isLatestRun(runId): DOM-írás előtt ellenőrzés
   ========================================================================== */

// "14,29" → 14.29; trim; NaN esetén 0
const toNum = (v) => {
  const n = Number(String(v ?? '').replace(',', '.').trim());
  return Number.isFinite(n) ? n : 0;
};

let __currentController = null;
let __currentRunId = 0;
let __isBusy = false;

const __disableUI = () => {
  if (__isBusy) return;
  __isBusy = true;
  document
      .querySelectorAll(
          '.form__input--visualizer, .mass-prop-input, .btn, button, input[type="button"]'
      )
      .forEach((el) => {
        el.disabled = true;
      });
  document.body.classList.add('busy-cursor'); // opcionális CSS: .busy-cursor, cursor: progress;
};

const __enableUI = () => {
  __isBusy = false;
  document
      .querySelectorAll(
          '.form__input--visualizer, .mass-prop-input, .btn, button, input[type="button"]'
      )
      .forEach((el) => {
        el.disabled = false;
      });
  document.body.classList.remove('busy-cursor');
};

export const beginRun = () => {
  if (__currentController) {
    try {
      __currentController.abort(); // lelövi a futó HTTP-kéréseket
    } catch {
      /* no-op */
    }
  }
  __currentController = new AbortController();
  __currentRunId += 1;
  __disableUI();
  return { runId: __currentRunId, signal: __currentController.signal };
};

export const endRun = (runId) => {
  // Csak a legfrissebb futás oldhatja fel a UI-t
  if (runId !== __currentRunId) return;
  __enableUI();
};

export const isLatestRun = (runId) => runId === __currentRunId;

/* ============================================================================
   Helper: új frakció sor HTML-je
   ========================================================================== */
const newFraction = (ID, nComp, time) => {
  let NF = `<tr class="fractions" id="row_${ID}">
<td>Fraction ${ID + 1}</td>
<td><input type="number" name="startTime" id="st_${ID}" class="start-time form__input form__input--visualizer" step="0.1" value="${(
      ID * time
  ).toFixed(1)}"></td>
<td><input type="number" name="endTime" id="et_${ID}" class="end-time form__input form__input--visualizer" step="0.1" value="${(
      (ID + 1) * time
  ).toFixed(1)}"></td>`;

  for (let index = 0; index < nComp; index++) {
    NF += `<td id="cFR_${ID}_${index}"></td>`;
  }
  // Helyes: nincs "unchecked" attribútum
  NF += `<td><input type="checkbox" name="fraction_selected" class="fraction-selctor" id="frSel_${ID}"></td></tr>`;
  return NF;
};

/* ============================================================================
   Mass proportion normalizálás (Σ=1)
   - A táblázat "concentration" mezői tömegarányt tartalmaznak (nem mg/ml, nem %).
   - Itt normalizáljuk Σ=1-re, és ebből számoljuk a tényleges injektált mg-okat.
   ========================================================================== */
const readMassProportions = (rows) => {
  const props = [];
  for (const r of rows) {
    const raw = r.querySelector('input[name="concentration"]')?.value;
    let p = toNum(raw);
    if (p < 0) p = 0;
    props.push(p);
  }
  const sum = props.reduce((s, x) => s + x, 0);
  if (sum <= 0) {
    const eq = 1 / Math.max(1, props.length);
    return props.map(() => eq);
  }
  return props.map((p) => p / sum);
};

/* ============================================================================
   UI: normalizált w/w% kiírása az input melletti spanba
   ========================================================================== */
const updateMassPercentUI = (rows, fractions) => {
  Array.from(rows).forEach((r, idx) => {
    const span = r.querySelector('.mass-prop-percent');
    if (!span) return;
    const p = fractions[idx] * 100; // fi ∈ [0..1] → %
    span.textContent = `${p.toLocaleString('hu-HU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} %`;
  });
};

/* ============================================================================
   Frakciók kezelése (UI, események, tartalom frissítés)
   - runId guard: csak a legfrissebb futás frissítheti a DOM-ot
   ========================================================================== */
export const handleFractions = (compounds, settings, plotSettings, runId) => {
  const b = document.getElementById('btnCreateFr');
  const frTableBody = document.getElementById('fraction_tablebody');
  const fractionTime = Math.floor(settings.runtime / 9);

  // Ez az event egyszerre többször ne drótozódjon fel
  b.replaceWith(b.cloneNode(true));
  const b2 = document.getElementById('btnCreateFr');

  b2.addEventListener('click', () => {
    if (!isLatestRun(runId)) return; // stale védelem

    const head = document.querySelector('#fraction_table thead tr');
    head.innerHTML = '<th>Fraction</th><th>Start Time</th><th>End Time</th>';

    compounds.forEach((c) => {
      const compoundHeading = document.createElement('th');
      compoundHeading.innerHTML = c.compoundName;
      head.appendChild(compoundHeading);
    });
    const selectHeading = document.createElement('th');
    selectHeading.innerHTML = 'Selected';
    head.appendChild(selectHeading);

    let bodyContent = '';
    for (let index = 0; index < 9; index++) {
      bodyContent += newFraction(index, compounds.length, fractionTime.toFixed(1));
    }
    frTableBody.innerHTML = bodyContent;

    const startTimes = document.querySelectorAll('.start-time');
    const endTimes = document.querySelectorAll('.end-time');
    const selectSwitch = document.querySelectorAll('.fraction-selctor');
    const lastId = startTimes.length - 1;

    for (const startTime of startTimes) {
      startTime.addEventListener('input', (ev) => {
        if (!isLatestRun(runId)) return;
        const idx = ev.target.id.substring(3) * 1;
        const end = endTimes[idx];
        const prevEnd = idx > 0 ? endTimes[idx - 1].value : 0;

        end.setAttribute('min', ev.target.value);
        ev.target.setAttribute('max', end.value);
        ev.target.setAttribute('min', prevEnd);

        plot.plotFractions(readFractions(compounds, settings), plotSettings);
        updateContent(compounds, settings, runId);
      });
    }

    for (const endTime of endTimes) {
      endTime.addEventListener('input', (ev) => {
        if (!isLatestRun(runId)) return;
        const idx = ev.target.id.substring(3) * 1;
        const start = startTimes[idx];
        if (idx === lastId) {
          ev.target.value = settings.runtime;
          ev.target.setAttribute('max', settings.runtime);
        } else {
          const nextStart = startTimes[idx + 1];
          nextStart.setAttribute('min', ev.target.value);
          ev.target.setAttribute('max', nextStart.value);
        }
        start.setAttribute('max', ev.target.value);
        ev.target.setAttribute('min', start.value);

        plot.plotFractions(readFractions(compounds, settings), plotSettings);
        updateContent(compounds, settings, runId);
      });
    }

    for (const s of selectSwitch) {
      s.addEventListener('input', (ev) => {
        // újrarajzolás
        plot.plotFractions(readFractions(compounds, settings), plotSettings);

        const row = ev.target.closest('tr');
        if (row) row.style.background = ev.target.checked ? '#25bdad' : '#fff';

        // --- Summary panel biztosítása ---
        let summary = document.querySelector('.results');
        if (!summary) {
          // ha nincs, létrehozzuk és betesszük a frakciós dobozba
          const host = document.querySelector('.fraction-table') || document.body;
          summary = document.createElement('div');
          summary.className = 'results';
          summary.style.display = 'none';
          summary.innerHTML = `
        <div class="white-box" style="margin-top:12px">
          <h3>Selection summary</h3>
          <table class="table" id="summary-table">
            <thead>
              <tr>
                <th>Compound</th>
                <th>Injected (mg)</th>
                <th>Purity %</th>
                <th>Recovery %</th>
              </tr>
            </thead>
            <tbody></tbody>
          </table>
        </div>`;
          host.appendChild(summary);
        }

        // most már biztosan létezik
        summary.style.display = 'block';

        // --- kiválasztott frakciók eredményeinek számítása ---
        const selected = readFractions(compounds, settings).filter((d) => d[2] === true);

        const results = [];
        for (let index = 0; index < compounds.length; index++) {
          const compound = compounds[index];
          let recovered = 0;
          let total = 0;
          for (let i = 0; i < selected.length; i++) {
            recovered += selected[i][3][index][1][0];
            total     += selected[i][3][index][1][1];
          }
          results.push({
            name: compound.compoundName,
            purity: total > 0 ? (recovered / total) * 100 : 0,
            recovery: compound.injAmount > 0
                ? ((recovered * settings.flowRate) / compound.injAmount) * 100
                : 0,
          });
        }

        // táblázat feltöltése
        createSummary(results, compounds, settings);
      });
    }

    if (!isLatestRun(runId)) return;
    plot.plotFractions(readFractions(compounds, settings), plotSettings);
    updateContent(compounds, settings, runId);
    b2.innerHTML = 'Reset fractions';
  });
};

/* ============================================================================
   Beállítások beolvasása (t0, tss, stb.)
   ========================================================================== */
export const getMethodSettings = () => {
  const flow     = toNum(document.getElementById('flow-rate')?.value);
  const sfVal    = toNum(document.getElementById('sf')?.value);
  const colVol   = toNum(document.getElementById('column-volume')?.value);
  const deadVol  = toNum(document.getElementById('dead-volume')?.value);
  const injVol   = toNum(document.getElementById('injection-volume')?.value);
  const totConc  = toNum(document.getElementById('total-concentration')?.value);
  const swMin    = toNum(document.getElementById('switch-time')?.value);
  const runtime  = toNum(document.getElementById('runtime')?.value);

  const Vcm = swMin * flow;
  const Vss = Vcm + (1 - sfVal / 100) * colVol;
  const tss = Vss / flow;

  const settings = {
    instrument: document.getElementById('instrument')?.value,
    columnVolume: colVol,
    sf: sfVal,
    flowRate: flow,
    mode: document.getElementById('mode')?.value,
    injectionVolume: injVol,
    sampleSolvent: document.getElementById('sample-solvent')?.value,
    totalConcentration: totConc,
    threshold: 0.01,
    method: document.getElementById('method')?.value,
    tsw: swMin,
    tss,
    v0: deadVol,
    t0: flow > 0 ? deadVol / flow : 0,
  };

  settings.totalAmount = injVol * totConc;
  settings.CELLS = 100;
  settings.tc = flow > 0 ? colVol / flow : 0;
  settings.dt = flow > 0 ? (colVol * (1 - settings.sf / 100)) / (settings.CELLS * flow) : 0;
  settings.runtime = runtime;

  return settings;
};

/* ============================================================================
   UI: új komponens sor hozzáadása (egy darab mass proportion input + %-span)
   ========================================================================== */
export const addCompound = (id) => {
  const table = document.getElementById('compounds-table');
  const newRow = table.insertRow(-1);
  newRow.id = `row-${id}`;

  newRow.innerHTML = `
    <td>
      <input type="text" name="compound-name" id="compound-${id}"
             class="table-input table-input--text" value="compound-${id + 1}">
    </td>

    <td>
      <input type="number" name="partition-coefficient" id="kd-${id}"
             class="table-input" min="0" max="10" step="0.1" value="1">
    </td>

    <!-- Mass proportion (w/w) → egy darab input + a normalizált %-span -->
    <td>
      <div class="mass-prop-cell">
        <input type="number" name="concentration" id="concentration-${id}"
               class="table-input mass-prop-input" min="0" step="0.1" value="10">
        <span class="mass-prop-percent">(– %)</span>
      </div>
    </td>

    <!-- Injected amount (mg) – JS tölti ki -->
    <td>
      <div class="inj-amount" id="inj-amount-${id}">mg</div>
    </td>

    <td>
      <input type="number" class="table-input" name="solubility-up"
             id="sol-up-${id}" min="0" step="0.1" value="100">
    </td>

    <td>
      <input type="number" class="table-input" name="solubility-lp"
             id="sol-lp-${id}" min="0" step="0.1" value="100">
    </td>

    <td>
      <input type="button" value="Remove" id="delete-${id}"
             class="btn btn--blue btn--ct delete-btn">
    </td>
  `;
};

/* ============================================================================
   Backend számítások: megszakítható kérések (AbortController.signal)
   ========================================================================== */
export const getDataPoints = async (compound, methodSettings, signal) => {
  const res = await axios({
    method: 'POST',
    url: '/api/calc',
    data: { compound, methodSettings },
    withCredentials: true,
    signal // <-- fontos: így megszakítható
  });
  if (res.data.status === 'success') {
    return res.data.data; // [[t, c], ...] – t még t0 nélkül
  }
  return [];
};

export const analysePeak = async (compound, settings, signal) => {
  const res = await axios({
    method: 'POST',
    url: '/api/calc/analyse',
    data: { compound, settings },
    withCredentials: true,
    signal // <-- fontos
  });
  if (res.data.status === 'success') {
    return res.data.data;
  }
  return null;
};

/* ============================================================================
   Segédfüggvények: csúcsparaméterek (t0-val korrigált RT)
   ========================================================================== */
const extractColumn = (arr, column) => arr.map((x) => x[column]);

export const calcPeakParams = (compound, settings) => {
  const height = Math.max(...extractColumn(compound.dataPoints, 1));
  const vr =
      settings.columnVolume *
      (1 + ((compound.partitionCoefficient - 1) * settings.sf) / 100);
  const rt = vr / settings.flowRate + settings.t0; // ✅ t0 hozzáadása
  return { height, vr, rt };
};

export const inputHandleInstrumentDefaults = () => {
  document.getElementById('instrument').addEventListener('change', () => {
    const instrument = document.getElementById('instrument').value;
    if (instrument === 'labCPC') {
      document.getElementById('column-volume').value = 250;
      document.getElementById('sf').value = 80;
      document.getElementById('flow-rate').value = 20;
      document.getElementById('injection-volume').value = 10;
    }
    if (instrument === 'rCPC') {
      document.getElementById('column-volume').value = 2150;
      document.getElementById('sf').value = 80;
      document.getElementById('flow-rate').value = 220;
      document.getElementById('injection-volume').value = 80;
    }
    if (instrument === 'iCPC') {
      document.getElementById('column-volume').value = 22500;
      document.getElementById('sf').value = 80;
      document.getElementById('flow-rate').value = 2200;
      document.getElementById('injection-volume').value = 800;
    }
  });
};

export const inputHandleSf = () => {
  const sf = document.getElementById('sf');
  if (sf) {
    sf.addEventListener('change', () => {
      document.querySelector('.sf-label').innerText = `${sf.value}%`;
    });
  }
};

/* ============================================================================
   Komponensek beolvasása a táblázatból (futás-azonosító és signal használatával)
   - MASS PROPORTION NORMALIZÁLÁS (fi = ratio_i / Σ ratio)
   - injAmount_i = fi * (InjectionVolume * TotalConcentration)
   - datapontokhoz t0 hozzáadása
   ========================================================================== */
export const getCompounds = async (compoundTable, settings, signal, runId) => {
  const compounds = [];
  const rows = compoundTable.querySelectorAll('tbody tr');

  // fi ∈ [0..1], Σ fi = 1
  const fractions = readMassProportions(rows);
  const totalInjectedMg = toNum(settings.injectionVolume) * toNum(settings.totalConcentration);

  let rowIdx = 0;
  for (const r of rows) {
    // --- Kd beolvasás + üzemmód ---
    const kdInput = toNum(r.querySelector('input[name="partition-coefficient"]')?.value);
    let partitionCoefficient = kdInput;
    if (settings.mode === 'asc' && kdInput > 0) {
      partitionCoefficient = 1 / kdInput; // ASC módban 1/Kd
    }

    // --- meta ---
    const compoundName = (r.querySelector('input[name="compound-name"]')?.value || '').trim();
    const solubilityUp = toNum(r.querySelector('input[name="solubility-up"]')?.value);
    const solubilityLp = toNum(r.querySelector('input[name="solubility-lp"]')?.value);

    // --- normalizált hányad és injektált tömeg ---
    const fi = toNum(fractions[rowIdx]);       // 0..1
    const injAmount = fi * totalInjectedMg;    // mg

    // UI: injektált mg kiírása
    const injDiv = r.querySelector('div.inj-amount');
    if (injDiv) injDiv.innerText = `${injAmount.toFixed(2)} mg`;

    // --- compound objektum ---
    const compound = {
      compoundName,
      partitionCoefficient,
      k: partitionCoefficient,      // ⬅️ legyen saját k mező is
      concentration: fi,
      solubilityUp,
      solubilityLp,
      injAmount
    };

    // Backend datapontok → t0 hozzáadása
    const rawPoints = await getDataPoints(compound, settings, signal);
    if (!isLatestRun(runId)) return []; // ha közben új futás indult, ne folytassuk

    compound.dataPoints = rawPoints.map(([t, c]) => [toNum(t) + toNum(settings.t0), toNum(c)]);

    // Csúcsparaméterek
    const { height, vr, rt } = calcPeakParams(compound, settings);
    compound.height = height;
    compound.vr = vr;
    compound.rt = rt;

    compounds.push(compound);
    rowIdx += 1;
  }

  // GUI: normalizált %-ok kiírása
  updateMassPercentUI(rows, fractions);

  return compounds;
};

/* ============================================================================
   Peak details táblázat – teljes újrarajzolás (duplikátumok ellen)
   ========================================================================== */
export const renderPeakDetails = (compounds, runId) => {
  if (!isLatestRun(runId)) return;
  const tbody = document.querySelector('#peak-details tbody');
  if (!tbody) return;

  // 1) Biztos törlés
  tbody.replaceChildren();

  // 2) Új sorok beszúrása
  for (const compound of compounds) {
    const tr = document.createElement('tr');
    tr.innerHTML = `
      <td>${compound.compoundName}</td>
      <td>${(compound.rt ?? 0).toFixed(2)}</td>
      <td>${(compound.vr ?? 0).toFixed(2)}</td>
      <td>${compound.widthVolume?.toFixed ? compound.widthVolume.toFixed(2) : 'N.A.'}</td>
      <td>${compound.thPlates?.toFixed ? compound.thPlates.toFixed(0) : 'N.A.'}</td>
      <td>${compound.rsNext ? compound.rsNext.toFixed(2) : 'N.A.'}</td>
    `;
    tbody.appendChild(tr);
  }
};

/* ============================================================================
   Csúcsok sorrendje (opcionális támogatás)
   ========================================================================== */
export const calcPeakOrder = (compounds) => {
  const pA = compounds
      .map((e) => e.peakApex)
      .slice()
      .sort((a, b) => a - b);
  let order = 1;
  for (let i = 0; i < pA.length; i += 1) {
    compounds.forEach((el) => {
      if (el.peakApex === pA[i]) {
        el.orderNo = order;
      }
    });
    order += 1;
  }
  return compounds;
};

/* ============================================================================
   Frakciók kiolvasása + integrálás (jelenlegi nem-adaptív megoldás)
   ========================================================================== */
export const readFractions = (compounds, settings) => {
  const fractionTimes = [];
  const fractions = document.querySelectorAll('.fractions');

  for (const f of fractions) {
    const start = toNum(f.querySelector('input[name="startTime"]')?.value);
    const end   = toNum(f.querySelector('input[name="endTime"]')?.value);
    const selected = f.querySelector('input[name="fraction_selected"]').checked;
    const compositon = [];

    for (let index = 0; index < compounds.length; index++) {
      const content = calcAreaPerc(
          compounds,
          index,
          Math.round(start / settings.dt),
          Math.round(end / settings.dt),
          settings.dt,
          0.01
      );
      compositon.push([compounds[index].compoundName, content]);
    }

    fractionTimes.push([start, end, selected, compositon]);
  }

  return fractionTimes;
};

// Integrálás: egyszerű (baloldali) + dt-alapú; biztonságos indexekkel
const integrate = (dataPoints, start, end, step, threshold) => {
  let sum = 0;
  const maxIndex = dataPoints.length - 1;
  const safeStart = Math.max(0, Math.min(start, maxIndex));
  const safeEnd = Math.max(0, Math.min(end, maxIndex));

  for (let index = safeStart; index < safeEnd; index += 1) {
    if (dataPoints[index][1] >= threshold) {
      if (index > 0) {
        sum += (dataPoints[index][0] - dataPoints[index - 1][0]) * dataPoints[index][1];
      } else if (index + 1 <= maxIndex) {
        sum += (dataPoints[index + 1][0] - dataPoints[index][0]) * dataPoints[index][1];
      }
    }
  }
  return sum;
};

// Integrálás több komponensre → [adott_komponens_területe, összterület]
const calcAreaPerc = (compounds, peakId, peakStart, peakEnd, step, threshold) => {
  const areas = [];
  let totalArea = 0;

  for (const compound of compounds) {
    const currentArea = integrate(compound.dataPoints, peakStart, peakEnd, step, threshold);
    areas.push(currentArea);
    totalArea += currentArea;
  }
  return [areas[peakId], totalArea];
};

// Frakció tartalom cellák frissítése (mg)
const updateContent = (compounds, settings, runId) => {
  if (!isLatestRun(runId)) return;
  const contents = readFractions(compounds, settings);

  for (let i = 0; i < compounds.length; i++) {
    for (let j = 0; j < contents.length; j++) {
      const cell = document.getElementById(`cFR_${j}_${i}`);
      const mg = contents[j][3][i][1][0] * settings.flowRate; // terület × áramlás ≈ mennyiség
      if (cell) cell.innerHTML = `${mg.toFixed(2)} mg`;
    }
  }
};

// (opcionális) Összefoglaló táblázat
const createSummary = (results, compounds, settings) => {
  const summaryTable = document.querySelector('#summary-table tbody');
  if (!summaryTable) return;
  summaryTable.replaceChildren(); // biztos törlés

  results.forEach((res, i) => {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td>${res.name}</td>
      <td>${compounds[i].injAmount.toFixed(2)} mg</td>
      <td>${res.purity.toFixed(2)} %</td>
      <td>${res.recovery.toFixed(2)} %</td>
    `;
    summaryTable.appendChild(row);
  });

  const avgPurity = results.reduce((s, r) => s + r.purity, 0) / results.length;
  const avgRecovery =
      results.reduce((s, r) => s + r.recovery, 0) / results.length;

  const totalRow = document.createElement('tr');
  totalRow.innerHTML = `
    <td><b>Átlag</b></td>
    <td>-</td>
    <td><b>${avgPurity.toFixed(2)} %</b></td>
    <td><b>${avgRecovery.toFixed(2)} %</b></td>
  `;
  summaryTable.appendChild(totalRow);
};

/* ============================================================================
   (Opcionális) Debounce helper a hívó oldalnak
   ========================================================================== */
export const debounce = (fn, delay = 250) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
};

/* ============================================================================
   HÍVÁSI MINTA A HÍVÓ KÓDBAN (pl. index.js):
   ---------------------------------------------------------------------------
   const runSimulation = async () => {
     const { runId, signal } = beginRun();
     try {
       const settings = getMethodSettings();
       const compounds = await getCompounds(table, settings, signal, runId);
       if (!isLatestRun(runId)) return;

       // ... plotolás, frakciók, stb. ...
       renderPeakDetails(compounds, runId);

     } catch (err) {
       if (err.name === 'CanceledError' || err.message === 'canceled') {
         // megszakított kérés → nincs teendő
       } else {
         console.error(err);
       }
     } finally {
       endRun(runId);
     }
   };

   visualizerInput.addEventListener('change', debounce(runSimulation, 250));
   ========================================================================== */

// CSV export restored from the last known working visualizer implementation.
export const exportData = (exportRows) => {
  if (!Array.isArray(exportRows) || exportRows.length === 0 || !Array.isArray(exportRows[0])) return;
  const rows = [];
  const rowCount = exportRows[0].length;
  for (let n = 0; n < rowCount; n += 1) {
    const row = [];
    for (let index = 0; index < exportRows.length; index += 1) {
      row.push((exportRows[index][n] || []).join(','));
    }
    rows.push(row.join(','));
  }
  const blob = new Blob([rows.join('\n') + '\n'], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'cpc-simulation.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
